/**
 * Modules Settings Tab — Phase 12
 *
 * Lets users show or hide sidebar modules.
 * Hidden modules are not deleted — data is preserved.
 * Changes persist immediately via PUT /api/me/settings/modules.
 */
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { useOnboardingStatus, ONBOARDING_QUERY_KEY } from '@/hooks/useOnboardingStatus';
import type { OnboardingStatusResponse } from '@/lib/schemas';

// All configurable modules in display order
const ALL_MODULES = [
  {
    id: 'proposals',
    label: 'Proposals',
    description: 'Proposal pipeline and status tracking',
  },
  {
    id: 'site_visits',
    label: 'Site Visits',
    description: 'Field visits, observations, and photos',
  },
  {
    id: 'rfis',
    label: 'RFIs',
    description: 'Requests for information',
  },
  {
    id: 'submittals',
    label: 'Submittals',
    description: 'Shop drawings and submittal reviews',
  },
  {
    id: 'portal',
    label: 'Client Portal',
    description: 'External client portal management',
  },
];

export function ModulesSettingsTab() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useOnboardingStatus();
  const onboardingData = data as OnboardingStatusResponse | undefined;

  // Track in-flight module IDs to prevent double-clicks
  const [saving, setSaving] = useState<Set<string>>(new Set());

  const visibility: Record<string, boolean> = onboardingData?.module_visibility ?? {};

  // A module is visible if it's missing from the map (default true) or explicitly true
  const isVisible = (moduleId: string) => visibility[moduleId] !== false;

  const handleToggle = async (moduleId: string, checked: boolean) => {
    if (saving.has(moduleId)) return;

    setSaving(prev => new Set(prev).add(moduleId));
    const updated = { ...visibility, [moduleId]: checked };

    try {
      await api.onboarding.updateModules(updated);
      queryClient.invalidateQueries({ queryKey: ONBOARDING_QUERY_KEY });
    } catch {
      toast.error('Failed to update module visibility');
    } finally {
      setSaving(prev => {
        const next = new Set(prev);
        next.delete(moduleId);
        return next;
      });
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4 max-w-lg">
        {ALL_MODULES.map(m => (
          <Skeleton key={m.id} className="h-14 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="max-w-lg space-y-2">
      <p className="text-sm text-muted-foreground mb-4">
        Hidden modules are not deleted — your data is preserved. Toggle them back on anytime.
      </p>

      {ALL_MODULES.map(mod => {
        const checked = isVisible(mod.id);
        const isSaving = saving.has(mod.id);

        return (
          <div
            key={mod.id}
            className="flex items-start gap-3 rounded-lg border p-4"
          >
            <Checkbox
              id={`module-${mod.id}`}
              checked={checked}
              disabled={isSaving}
              onCheckedChange={(value) => handleToggle(mod.id, value === true)}
              className="mt-0.5"
            />
            <div className="flex-1 min-w-0">
              <Label
                htmlFor={`module-${mod.id}`}
                className="text-sm font-medium cursor-pointer"
              >
                {mod.label}
              </Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                {mod.description}
              </p>
            </div>
            {isSaving && (
              <span className="text-xs text-muted-foreground self-center">Saving…</span>
            )}
          </div>
        );
      })}
    </div>
  );
}
